/**
 * 摄像头管理 Store 模块
 */
import {
  getCameraList,
  addCamera,
  deleteCamera,
  updateCamera,
  getCameraDetail,
  saveScreenLayout,
  getScreenLayout,
  uploadVideo,
  pingCamera,
  batchPingCameras,
  discoverOnvifDevices,
  addOnvifCamera,
  getRtmpConfig,
} from '@/api/camera';

export default {
  namespaced: true,
  state: {
    cameraList: [],
    total: 0,
    page: {
      pageNum: 1,
      pageSize: 10,
    },
    // 当前选择的摄像头
    selectedCamera: null,
    // 屏幕布局配置
    screenLayout: null,
    // 摄像头在线状态缓存 { camera_id: boolean }
    onlineStatus: {},
  },
  mutations: {
    SET_CAMERA_LIST(state, data) {
      state.cameraList = data.list || [];
      state.total = data.total || 0;
    },
    SET_PAGE(state, data) {
      state.page = { ...state.page, ...data };
    },
    SET_SELECTED_CAMERA(state, camera) {
      state.selectedCamera = camera;
    },
    SET_SCREEN_LAYOUT(state, layout) {
      state.screenLayout = layout;
    },
    SET_ONLINE_STATUS(state, { camera_id, online }) {
      state.onlineStatus[camera_id] = online;
    },
    CLEAR_ONLINE_STATUS(state) {
      state.onlineStatus = {};
    },
    ADD_CAMERA(state, camera) {
      state.cameraList.unshift(camera);
      state.total++;
    },
    UPDATE_CAMERA(state, camera) {
      const index = state.cameraList.findIndex(
        (item) => item.camera_id === camera.camera_id
      );
      if (index !== -1) {
        state.cameraList.splice(index, 1, camera);
      }
    },
    DELETE_CAMERA(state, cameraId) {
      const index = state.cameraList.findIndex(
        (item) => item.camera_id === cameraId
      );
      if (index !== -1) {
        state.cameraList.splice(index, 1);
        state.total--;
      }
    },
  },
  actions: {
    // 获取摄像头列表
    async getCameraList({ commit }, data) {
      try {
        const res = await getCameraList(data);
        if (res && res.code === 0) {
          commit('SET_CAMERA_LIST', res.data);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 添加摄像头
    async addCamera(_context, data) {
      try {
        const res = await addCamera(data);
        if (res && res.code === 0) {
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 删除摄像头
    async deleteCamera({ commit }, data) {
      try {
        const res = await deleteCamera(data);
        if (res && res.code === 0) {
          commit('DELETE_CAMERA', data.camera_id);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 更新摄像头
    async updateCamera({ commit }, data) {
      try {
        const res = await updateCamera(data);
        if (res && res.code === 0) {
          commit('UPDATE_CAMERA', data);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 获取摄像头详情
    async getCameraDetail({ commit }, data) {
      try {
        const res = await getCameraDetail(data);
        if (res && res.code === 0) {
          commit('SET_SELECTED_CAMERA', res.data);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 保存屏幕布局
    async saveScreenLayout({ commit }, data) {
      try {
        const res = await saveScreenLayout(data);
        if (res && res.code === 0) {
          commit('SET_SCREEN_LAYOUT', data);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 获取屏幕布局
    async getScreenLayout({ commit }, data) {
      try {
        const res = await getScreenLayout(data);
        if (res && res.code === 0) {
          commit('SET_SCREEN_LAYOUT', res.data);
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 上传本地视频
    async uploadVideo(_context, formData) {
      try {
        const res = await uploadVideo(formData);
        if (res && res.code === 0) {
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 检测单个摄像头在线状态
    async pingCamera({ commit }, data) {
      try {
        const res = await pingCamera(data);
        if (res && res.code === 0) {
          commit('SET_ONLINE_STATUS', { camera_id: data.camera_id, online: res.data.online });
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 批量检测摄像头在线状态
    async batchPingCameras({ commit }, data) {
      try {
        const res = await batchPingCameras(data);
        if (res && res.code === 0) {
          res.data.results.forEach(({ camera_id, online }) => {
            commit('SET_ONLINE_STATUS', { camera_id, online });
          });
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 发现 ONVIF 设备
    async discoverOnvifDevices(_context, data) {
      try {
        const res = await discoverOnvifDevices(data);
        if (res && res.code === 0) {
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 添加 ONVIF 设备
    async addOnvifCamera(_context, data) {
      try {
        const res = await addOnvifCamera(data);
        if (res && res.code === 0) {
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },

    // 获取 RTMP 服务器配置
    async getRtmpConfig(_context, data) {
      try {
        const res = await getRtmpConfig(data);
        if (res && res.code === 0) {
          return res;
        } else {
          return Promise.reject(res);
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },
  },
};
